/**
 * <TutorFilmstrip> — la pellicule, la colonne de gauche du live du masteur (organism).
 *
 * La piste B du round 2, validée par Wajih le 02/11 : les visages à gauche, une vignette vidéo par
 * élève (`<TutorStudentTile>`), dans l'ordre du roster, et **la mienne en bas, toujours visible**, à la
 * même largeur (« Tu présentes » quand je présente, « Écran partagé » quand je partage mon écran).
 *
 * L'élève regardé s'ouvre sur place : sa vidéo, sa bascule, puis ses activités
 * (`<TutorLiveActivityList>`), sans menu ni onglet — le réflexe de l'ancien focus, où la liste d'un
 * seul élève se tenait à gauche du centre. Toucher une autre vidéo y amène sa liste.
 *
 * Les vidéos prennent la largeur de la pellicule, à un rapport fixe ; la hauteur de la pellicule, assez
 * pour une tablette en portrait (`TALL_FROM`), les agrandit et ouvre « Faites aujourd'hui » quand la
 * liste se monte. Ce qui ne tient pas défile dans la pellicule, jamais la page ; ma vidéo reste au pied.
 *
 * Round 3 (Wajih) : un élève qu'on ne regarde pas porte une petite barre, l'activité qu'il a devant lui
 * (`<TutorStudentTile>`). « Ajouter une activité » et « Son profil » sont au centre.
 *
 * Daily du 04/11 (Wajih) : **les cartes des élèves qu'on ne regarde pas sont plus petites** que celle de l'élève
 * regardé (`compact` : leur vidéo puis, dessous, une ligne avec leur activité, sans liste — test du 04/11
 * après-midi : la miniature à côté de l'activité ne se lisait pas) ; la liste de l'élève regardé
 * ne montre que son activité en cours et la suivante (`condensed`, le reste derrière « Voir tout ») ; les
 * résumés du champion quittent la colonne (qui veut les lire les trouve dans son fil) ; une main levée pendant
 * un Dialogue avec un autre élève offre sur sa carte les deux réponses de l'ancienne interface (`onHandChoice`).
 *
 * Les autres tuteurs de la séance (un second tuteur, un membre de l'équipe) ont chacun leur tuile au pied,
 * au-dessus de la mienne et à sa taille (`coTutors`) : l'ancien live les montrait, chacun voyait l'autre.
 *
 * « À traiter » occupe la colonne du champion : **chaque tuile porte le fil de sa séance et « Liste · n »**, qui déplie
 * sa liste sur place (regardée ou non) ; **« Tous »** en tête de colonne (à partir de deux élèves) ouvre la
 * mosaïque. Changer d'élève se fait ici, dans la mosaïque, ou par « Voir <prénom> » sur une carte.
 *
 * Contrôlée : l'élève regardé et l'activité ouverte au centre arrivent en props.
 */
import React, { useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UILiveParticipant, type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { Badge } from "../../atoms/Badge"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { ScrollView } from "../../atoms/ScrollView"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { TutorLiveActivityList } from "../../molecules/TutorLiveActivityList"
import { TutorStudentTile, type TutorHandChoice } from "../../molecules/TutorStudentTile"
import { tutorMeParticipant } from "../../molecules/TutorStudentVideo"
import { VideoTile } from "../../molecules/VideoTile"

/** Ce que ma vignette dit de moi : je présente le centre, ou je partage mon écran. */
export type TutorLiveMeState = "presenting" | "sharing"

export type TutorFilmstripProps = {
    students: UITutorLiveStudent[]
    selectedId?: string
    onSelect: (studentId: string) => void
    onChangeMode: (studentId: string, mode: UISoundMode) => void
    /** L'élève dont la bascule du mode part. */
    modePendingId?: string
    /** L'activité ouverte au centre : sa ligne est teintée dans la liste. */
    openActivityId?: string
    onOpenActivity: (studentId: string, activityId: string) => void
    /** Le fil d'un élève touché : son onglet « Fil » au centre, à la ligne d'un point ou sur maintenant. */
    onOpenThread?: (studentId: string, eventId?: string) => void
    /** Réordonner « À faire » de l'élève regardé. Absent : pas de glisser. */
    onReorder?: (studentId: string, activityIds: string[]) => void
    /** « Voir plus » sous ses faites. Absent : toutes. */
    onSeeMoreDone?: (studentId: string) => void
    /** Ouvrir le fil du chat d'un élève (ses non-lus, sur sa vidéo). */
    onOpenChat?: (studentId: string) => void
    /** Le prénom du champion qui aide en séance (le monde de jeu de l'espace) : « Envoyer … ». */
    helperName?: string
    /** Répondre à la main levée d'un élève pendant un Dialogue avec un autre. Absent : pas de réponse. */
    onHandChoice?: (studentId: string, choice: TutorHandChoice) => void
    /**
     * La place que garde l'en-tête à gauche pour le chevron qui replie la pellicule, posé par la page à la place exacte
     * de celui qui la déplie (`COLUMN_TOGGLE`). Défaut : aucune.
     */
    toggleRoom?: number
    /** « Tous » : voir tout le groupe (la mosaïque). Absent, ou un seul élève : pas de « Tous ». */
    onShowAll?: () => void
    me: UILiveParticipant
    /** Les autres tuteurs connectés à la séance, au-dessus de ma tuile. Défaut : aucun. */
    coTutors?: UILiveParticipant[]
    meState?: TutorLiveMeState
    /** La vraie piste d'un participant (`me.id` pour la mienne). Absente : sa photo, ou son visage. */
    renderVideo?: (participantId: string) => React.ReactNode
    testID?: string
}

const TEXTS = {
    title: (count: number) => `Élèves · ${count}`,
    presenting: "Tu présentes",
    sharing: "Écran partagé",
    all: "Tous",
    allLabel: "Voir tout le groupe",
}

/** À partir de cette hauteur (une tablette en portrait), les vidéos grandissent et « Faites » s'ouvre. */
const TALL_FROM = 900

/**
 * Le rapport largeur / hauteur des vidéos : l'élève regardé, les autres, moi ; plus hautes en portrait. Chaque
 * élève, regardé ou non, connecté ou non, a sa vidéo en 16:9, où sa caméra tient entière (`contain`) : à 3,3 et
 * 2,2, les vignettes étaient de minces bandes où l'image se coupait (Wajih, live en prod du 03/11). Un élève
 * qu'on ne regarde pas l'a sur toute la largeur de sa carte, son activité dessous (`compact`). La mienne reste
 * petite, sa caméra entière dedans.
 */
const ASPECT = {
    low: { selected: 16 / 9, other: 16 / 9, me: 2.4 },
    tall: { selected: 1.6, other: 1.6, me: 1.7 },
}

export function TutorFilmstrip(props: TutorFilmstripProps) {
    const {
        students,
        selectedId,
        onSelect,
        onChangeMode,
        modePendingId,
        openActivityId,
        onOpenActivity,
        onOpenThread,
        onReorder,
        onSeeMoreDone,
        onOpenChat,
        helperName,
        onHandChoice,
        toggleRoom = 0,
        onShowAll,
        me,
        coTutors,
        meState,
        renderVideo,
        testID,
    } = props
    // État d'interface : la pellicule a-t-elle la hauteur d'une tablette en portrait ? Gardé en booléen : la
    // vue ne se redessine qu'au passage du seuil. `null` avant la mesure : la liste attend de savoir où
    // « Faites aujourd'hui » part (ouvert si la pellicule est haute), puis garde ce que le masteur en fait.
    const [tall, setTall] = useState<boolean | null>(null)
    const aspect = tall === true ? ASPECT.tall : ASPECT.low
    const meVideo = renderVideo?.(me.id)

    return (
        <View
            style={styles.root}
            onLayout={(event) => setTall(event.nativeEvent.layout.height >= TALL_FROM)}
            testID={testID}
        >
            <View style={styles.head(toggleRoom)}>
                <SectionLabel>{TEXTS.title(students.length)}</SectionLabel>
                <View style={styles.flex} />
                {onShowAll != null && students.length > 1 && (
                    <ChoiceChip
                        label={TEXTS.all}
                        selected={false}
                        variant="pill"
                        action
                        onPress={onShowAll}
                        accessibilityLabel={TEXTS.allLabel}
                    />
                )}
            </View>
            <ScrollView style={styles.scroll} contentContainerStyle={styles.list}>
                {students.map((student) => {
                    const selected = student.id === selectedId
                    return (
                        <TutorStudentTile
                            key={student.id}
                            student={student}
                            videoAspect={selected ? aspect.selected : aspect.other}
                            selected={selected}
                            compact={!selected}
                            onSelect={() => onSelect(student.id)}
                            onChangeMode={(mode) => onChangeMode(student.id, mode)}
                            modePending={student.id === modePendingId}
                            video={renderVideo?.(student.id)}
                            onOpenActivity={(activityId) => onOpenActivity(student.id, activityId)}
                            onOpenThread={
                                onOpenThread != null ? (eventId) => onOpenThread(student.id, eventId) : undefined
                            }
                            onOpenChat={onOpenChat != null ? () => onOpenChat(student.id) : undefined}
                            onHandChoice={
                                onHandChoice != null ? (choice) => onHandChoice(student.id, choice) : undefined
                            }
                            helperName={helperName}
                            list={
                                <TutorLiveActivityList
                                    activities={student.activities}
                                    student={student}
                                    openActivityId={openActivityId}
                                    onOpenActivity={(activityId) => onOpenActivity(student.id, activityId)}
                                    onReorder={onReorder != null ? (ids) => onReorder(student.id, ids) : undefined}
                                    onSeeMoreDone={onSeeMoreDone != null ? () => onSeeMoreDone(student.id) : undefined}
                                    moreDone={student.moreDone}
                                    defaultDoneOpen={tall === true}
                                />
                            }
                        />
                    )
                })}
            </ScrollView>
            {coTutors?.map((tutor) => {
                const video = renderVideo?.(tutor.id)
                return (
                    <View key={tutor.id} style={styles.me(aspect.me)}>
                        <VideoTile
                            participant={tutorMeParticipant(tutor, video)}
                            size="tile"
                            video={video}
                            micMark="name"
                        />
                    </View>
                )
            })}
            <View style={styles.me(aspect.me)}>
                <VideoTile
                    participant={tutorMeParticipant(me, meVideo)}
                    size="tile"
                    me
                    video={meVideo}
                    micMark="name"
                />
                {meState != null && (
                    <View style={styles.meBadge} pointerEvents="none">
                        <Badge
                            text={meState === "presenting" ? TEXTS.presenting : TEXTS.sharing}
                            variant="primary"
                            iconName={meState === "presenting" ? "chalkboard-teacher" : "desktop"}
                            truncate={false}
                        />
                    </View>
                )}
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, gap: theme.spacing["2"] },
    head: (toggleRoom: number) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["1"],
        minHeight: 28,
        paddingLeft: toggleRoom,
    }),
    flex: { flex: 1 },
    scroll: { flex: 1, minHeight: 0 },
    // La pastille « à aller voir » d'une carte déborde de son coin haut droit : la place de la garder entière.
    list: {
        gap: theme.spacing["2"],
        paddingTop: theme.spacing["1"],
        paddingRight: theme.spacing["1"],
        paddingBottom: theme.spacing["1"],
    },
    me: (aspect: number) => ({ aspectRatio: aspect, borderRadius: theme.radius.md, overflow: "hidden" as const }),
    meBadge: { position: "absolute", top: theme.spacing["1"], left: theme.spacing["1"] },
}))
